import React, { useEffect, useRef, useState } from "react";

export default function LiveCrashChart({
  round, // { status, crashAt, waitUntil }
  multiplier, // number, current multiplier
  crashedAt, // number | null
  linePoints, // [{ t, m }, ...]
  cashouts = [], // [{ name, cashout }]
}) {
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(350);

  // --- FIXED AXIS VALUES ---
  const maxX = 40; // X-axis always 40 seconds
  const defaultMaxY = 30;
  const highest = Math.max(
    defaultMaxY,
    ...linePoints.map((p) => p.m),
    crashedAt || 0,
    multiplier || 0
  );
  const maxY = Math.ceil((highest + 2) / 10) * 10;

  useEffect(() => {
    function updateWidth() {
      const nextWidth = containerRef.current?.clientWidth || 350;
      setChartWidth(Math.max(260, Math.min(700, nextWidth)));
    }

    updateWidth();

    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const width = chartWidth;
  const height = width * 0.52;
  const px = (t) => (t / maxX) * (width - 70) + 55;
  const py = (m) => height - ((m - 1) / (maxY - 1)) * (height - 70) - 40;

  // Animated line
  const linePath =
    linePoints && linePoints.length > 1
      ? linePoints
          .map((p, i) => `${i === 0 ? "M" : "L"}${px(p.t)},${py(p.m)}`)
          .join(" ")
      : "";

  // Overlay timer
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    let timer;
    function update() {
      if (!round) return setSecondsLeft(0);
      if (round.status === "waiting" && round.waitUntil) {
        setSecondsLeft(
          Math.max(0, Math.ceil((round.waitUntil - Date.now()) / 1000))
        );
      } else if (round.status === "crashed") {
        setSecondsLeft(2); // replaced by real wait if backend supports
      } else {
        setSecondsLeft(0);
      }
    }
    update();
    timer = setInterval(update, 350);
    return () => clearInterval(timer);
  }, [round]);

  // Overlay message (NO div here!)
  let overlay = null;
  if (round?.status === "waiting" && secondsLeft > 0) {
    overlay = (
      <>
        <span
          role="img"
          aria-label="dot"
          className="inline-block align-middle"
          style={{ fontSize: 30, marginBottom: 2, marginRight: 8 }}
        >
          🟢 Betting closes in{" "}
          <span className="text-lime-400">{secondsLeft}s</span>
        </span>
      </>
    );
  } else if (round?.status === "crashed") {
    overlay = (
      <>
        <span
          role="img"
          aria-label="bolt"
          className="inline-block align-middle"
          style={{ fontSize: 22, marginBottom: 2, marginRight: 8 }}
        >
          ⚡ Next round starts soon...
        </span>
      </>
    );
  }

  // Multiplier display
  const showCrashed = crashedAt !== null && round?.status === "crashed";

  return (
    <div ref={containerRef} className="w-full">
      {/* Chart */}
      <div
        className="relative flex flex-col items-center justify-center rounded-3xl bg-[#171d26] shadow-xl"
        style={{
          width: width,
          height: height,
          maxWidth: "100vw",
          margin: "0 auto",
          overflow: "hidden", // prevents overflow on the sides
          minWidth: 0,
        }}
      >
        {/* SVG Line & Axis */}
        <svg width={width} height={height}>
          {/* Y-axis lines and labels */}
          {[...Array(9)].map((_, i) => {
            const y = 1 + ((maxY - 1) * i) / 8;
            return (
              <g key={i}>
                <line
                  x1={55}
                  x2={width - 20}
                  y1={py(y)}
                  y2={py(y)}
                  stroke="#242b35"
                  strokeDasharray="2 4"
                  strokeWidth={1}
                />
                <text
                  x={20}
                  y={py(y) + 6}
                  fontSize={width < 400 ? 11 : 15}
                  fill="#6ee7b7"
                  fontWeight="bold"
                  style={{ letterSpacing: "0.02em" }}
                >
                  {y.toFixed(1)}×
                </text>
              </g>
            );
          })}
          {/* X-axis labels */}
          {Array.from({ length: Math.ceil(maxX / 5) + 1 }).map((_, idx) => {
            const s = idx * 5;
            return (
              <text
                key={s}
                x={px(s)}
                y={height - 20}
                fontSize={width < 400 ? 11 : 14}
                fill="#9ca3af"
              >
                {s}s
              </text>
            );
          })}
          {/* Crash line */}
          <path
            d={linePath}
            stroke={showCrashed ? "#fa3434" : "#7fff7a"}
            strokeWidth={width < 400 ? 3 : 5}
            fill="none"
            style={{
              filter: showCrashed
                ? "drop-shadow(0 0 9px #fa343499)"
                : "drop-shadow(0 0 8px #84e76a80)",
              transition: "stroke 0.25s",
            }}
          />
          {/* Dot at tip */}
          {linePoints?.length > 0 && (
            <circle
              cx={px(linePoints[linePoints.length - 1].t)}
              cy={py(linePoints[linePoints.length - 1].m)}
              r={width < 400 ? 8 : 13}
              fill={showCrashed ? "#fa3434" : "#a3e635"}
              stroke="#fff"
              strokeWidth={3}
              style={{
                filter: showCrashed
                  ? "drop-shadow(0 0 7px #fa3434aa)"
                  : "drop-shadow(0 0 5px #baffb8)",
              }}
            />
          )}
          {/* Cashout dots */}
          {cashouts &&
            cashouts.map((c, idx) => {
              const tIdx = linePoints.findIndex((p) => p.m >= c.cashout);
              const t = tIdx !== -1 ? linePoints[tIdx].t : 0;
              const x = px(t),
                y = py(c.cashout);
              return (
                <g key={idx}>
                  <circle
                    cx={x}
                    cy={y}
                    r={width < 400 ? 6 : 10}
                    fill="#fbbf24"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                  <text
                    x={x + (width < 400 ? 6 : 12)}
                    y={y + 4}
                    fontSize={width < 400 ? 10 : 15}
                    fontWeight="bold"
                    fill="#f3f4f6"
                    style={{
                      paintOrder: "stroke",
                      stroke: "#171d26",
                      strokeWidth: width < 400 ? 2 : 3,
                    }}
                  >
                    {width < 400
                      ? `${c.name.slice(0, 6)}@${c.cashout}x`
                      : `${c.name} @${c.cashout}x`}
                  </text>
                </g>
              );
            })}
        </svg>

        {/* Multiplier or crash text */}
        {showCrashed ? (
          <div
            className="absolute left-1/2 top-1/2"
            style={{
              transform: "translate(-50%,-50%)",
              fontSize: width < 400 ? "1.5rem" : "clamp(2rem,6vw,3.5rem)",
              fontWeight: 600,
              color: "#fa3434",
              textShadow: "0 4px 32px #fa343444, 0 2px 8px #000c",
              zIndex: 2,
              userSelect: "none",
            }}
          >
            {crashedAt?.toFixed(2)}x
          </div>
        ) : (
          <div
            className="absolute left-1/2 top-1/2"
            style={{
              transform: "translate(-50%,-50%)",
              fontSize: width < 400 ? "1.5rem" : "clamp(2rem,6vw,3.5rem)",
              fontWeight: 900,
              color: "#fff",
              textShadow: "0 4px 32px #000a",
              zIndex: 2,
              userSelect: "none",
            }}
          >
            {multiplier?.toFixed(2)}x
          </div>
        )}
      </div>

      {/* Overlay below chart */}
      {overlay && (
        <div
          className="
            w-full max-w-lg mx-auto
            mt-3
            mb-3
            px-4 py-3
            rounded-xl
            bg-[#232d4299]
            text-center
            shadow-lg
            flex flex-col items-center justify-center
            font-bold
            text-white
            backdrop-blur
            border border-[#202b3a]/40
            text-lg md:text-2xl
          "
          style={{
            fontWeight: 900,
            letterSpacing: ".01em",
            position: "relative",
            zIndex: 1,
          }}
        >
          {overlay}
        </div>
      )}
    </div>
  );
}
